import {config} from "../config";
import { EventDataSource } from "./EventDataSource";

import { ListenerShell, IListenerShell } from "./ListenerShell";
import * as firebaseKeyCodec from "./firebaseKeyCodec";
import { EventQuestions } from "./_EventQuestions";
import { HttpResource } from "../resources/http.resource";
import { EventsManager } from "./eventsManager";
import { ServiceApi } from '../serviceApi';
import { Subscriber } from "rxjs/Subscriber";
import { client } from "../error";
import { ActiveAuthMissingError } from "../auth/activeAuth";
//for crop function

// var _buildPathBuilder = function () {
//     var pathArr = Array.prototype.slice.apply(arguments);
//     return function () {
//         var args = arguments;
//         return pathArr.map(function (arrEntry) {
//             if (arrEntry instanceof String || typeof arrEntry === "string") {
//                 return arrEntry;
//             }
//             else if (typeof arrEntry === "number") {
//                 return args[arrEntry];
//             }
//             else if (typeof arrEntry === "function") {
//                 return arrEntry.apply(null, args);
//             }
//         });
//     };
// };


/**
 * Represents a single event.
 * @constructor
 */
export class Event {

    private eventsManager: EventsManager;
    private eventId: string;
    private activeAuthKey = null;
    private currentSource = null;
    private dataSources = [];
    private telemetry;
    private firebase = null;
    private publicInfo = null;
    private eventInfo = null;
    private debug = false;

    constructor(eventsManager: EventsManager, eventId: string) {
        this.eventsManager = eventsManager;
        this.eventId = eventId;
        this.telemetry = this.eventsManager.getTelemetry().clone();
        this.updateAndDiffActiveAuthKey();
        this.loadEventInfo();
        this.loadPublicInfo();
    }

    public getEventsManager() {
        return this.eventsManager;
    }

    public getTelemetry() {
        if (this.debug) {
            return this.telemetry;
        }
        const unitFunction = function () {};
        return {
            clone: this.telemetry.clone.bind(this.telemetry),
            trace: unitFunction,
            debug: unitFunction,
            info: this.telemetry.info.bind(this.telemetry),
            warn: this.telemetry.warn.bind(this.telemetry),
            error: this.telemetry.error.bind(this.telemetry),
        };
    }

    public getEventId() {
        return this.eventId;
    };

    public getPromiseLib() {
        return this.eventsManager.getPromiseLib();
    };

    public allocateDataSource(id) {
        const dataSource = new EventDataSource(this, `allocate-${id}`, null);
        this.dataSources.push(dataSource);
        return dataSource;
    };

    public deallocateDataSource(ds) {
        this.detachDataSource(ds)
    };

    private detachDataSource(ds) {
        ds.stopAllListeners();

        const dsIdx = this.dataSources.indexOf(ds);
        if (dsIdx < 0) {
            return;
        }

        this.dataSources.splice(dsIdx, 1);
        ds.getTelemetry().debug("detached");
    };

    private loadPublicInfo_noRefresh() {
        if (!this.publicInfo) {
            return this.eventsManager.eventPublicInfoCache.find(this.eventId)
                .then((publicInfo) => {
                    this.publicInfo = publicInfo;
                    return this.loadPublicInfo_noRefresh();
                });
        }

        return this.eventsManager.getPromiseLib()((resolve) => {
            return resolve(this.publicInfo);
        });
    };

    private loadEventInfo_noRefresh() {
        if (!this.eventInfo) {
            return this.eventsManager.getEventInfoCache().find(this.eventId)
                .then((eventInfo) => {
                    this.eventInfo = eventInfo;
                    return this.loadEventInfo_noRefresh();
                });
        }

        return this.eventsManager.getPromiseLib()((resolve) => {
            return resolve(this.eventInfo);
        });
    };


    private loadEventInfo() {
        this.loadEventInfo_noRefresh().then(() => {
            this.getTelemetry().info("Full Info loaded, ensuring listener connections");
            this.ensureListenerConnections();
        }, (err) => {
            // YOLOing this, I'm pretty sure the instance of won't work, but we can at least check the name and status code
            // see https://bitbucket.org/joylife/joy/commits/486132ba51b4de7d09b8fe00547a87348c781b89
            if (err instanceof client.NotFound || err.statusCode === 404 || err.name === 'ActiveAuthMissingError' || err instanceof ActiveAuthMissingError) {
                // this is an expected scenario, no reason to pass the error, and it also won't be logged, 
                // see modules/app-browser/services/eventsManager/Event.js:181 (moved error handling to eventsManager...)
                return;
            }
            this.getTelemetry().error("Full info loaded failed. Might be ok if auth is not established");
            this.getTelemetry().error(err);
        });
    };

    private loadPublicInfo() {
        this.loadPublicInfo_noRefresh().then(() => {
            this.getTelemetry().info("Public Info loaded, ensuring listener connections");
            this.ensureListenerConnections();
        });
    };

    private reloadEventInfo() {
        this.eventInfo = null;
        this.loadEventInfo();
    };

    private reloadPublicInfo() {
        this.publicInfo = null;
        this.loadPublicInfo();
    };

    public getCachedPublicInfo() {
        return this.publicInfo;
    };

    public getCachedEventInfo() {
        return this.eventInfo;
    };

    public getFirebase() {
        return this.firebase;
    };

    public getExpectedDatabaseSource() {
        let expectedSource = null;
        if (this.firebase) {
            expectedSource = "firebase";
        }
        /*
        For now ignore eventInfo as a json source, let event connection connect to firebase.
        When we do the /me page, we will need to make this work.
         */
        // else if (This._eventInfo) {
        //     expectedSource = "eventInfo";
        // }
        // else if (This._publicInfo) {
        //     expectedSource = "publicInfo";
        // }
        return expectedSource;
    };


    private ensureListenerConnections() {
        this.dataSources.forEach((ds) => {
            ds._ensureListenerConnections();
        });
    };

    public createValue(path: Array<string>, value: string, callback) {
        if (this.getExpectedDatabaseSource() !== "firebase") {
            const err = new Error("Writes are currently only supported when connected to a database");
            return setImmediate(callback.bind(null, err));
        }

        if (path[0] !== "database") {
            const err = new Error("Writes are currently only supported on a database");
            return setImmediate(callback.bind(null, err));
        }

        const fb = path.slice(1).map(firebaseKeyCodec.encode).reduce((acc, curr) => {
            return acc.child(curr);
        }, this.getFirebase());

        const fbRef = fb.push();
        return fbRef.set(value, function () {
            var This = this;
            var args = arguments;
            setImmediate(function () {
                callback.apply(This, args);
            });
        }).then(() => fbRef);
    };

    public writeValue(path: Array<string>, value: string, callback) {
        if (this.getExpectedDatabaseSource() !== "firebase") {
            const err = new Error("Writes are currently only supported when connected to a database");
            return setImmediate(callback.bind(null, err));
        }

        if (path[0] !== "database") {
            const err = new Error("Writes are currently only supported on a database");
            return setImmediate(callback.bind(null, err));
        }

        const fb = path.slice(1).map(firebaseKeyCodec.encode).reduce((acc, curr) => {
            return acc.child(curr);
        }, this.getFirebase());

        return fb.set(value, function () {
            var This = this;
            var args = arguments;
            setImmediate(function () {
                callback.apply(This, args);
            });
        });
    };

    private updateValue_nonDatabase(path, value, callback) {
        if (path.length === 0 && ("currentPersonId") in value) {
            return this.setCurrentPersonId(value.currentPersonId).then(callback.bind(null, null)).catch(callback);
        }
        const err = new Error("Writes are currently only supported on a database");
        return setImmediate(callback.bind(null, err));
    };

    public updateValue(path, value, callback) {

        if (path[0] !== "database") {
            return this.updateValue_nonDatabase(path, value, callback);
        }

        if (this.getExpectedDatabaseSource() != "firebase") {
            const err = new Error("Writes are currently only supported when connected to a database");
            return setImmediate(callback.bind(null, err));
        }

        var fb = path.slice(1).map(firebaseKeyCodec.encode).reduce((acc, curr) => {
            return acc.child(curr);
        }, this.getFirebase());

        return fb.update(value, function () {
            var This = this;
            var args = arguments;
            setImmediate(function () {
                callback.apply(This, args);
            });
        });
    };

    public stopListener(l: IListenerShell) {
        l.detach();
    };

    public stopAllListeners(dataSource) {
        const ls: Array<IListenerShell> = dataSource._listeners;

        for (let i = ls.length - 1; i >= 0; i--) {
            ls[i].detach();
        };
        dataSource.getTelemetry().debug("stopped " + ls.length + " listeners");
    };

    public startValueListener(path: Array<string>, listener, dataSource) {
        const l = new ListenerShell(this, dataSource, path, "value", listener);
        l.ensureConnection();
        return l;
    };

    public startOnceValueListener(path: Array<string>, listener, dataSource) {
        const l = new ListenerShell(this, dataSource, path, "value", {
            onValue: function (value) {
                listener.onValue(value);
                dataSource.stopListener(l);
            },

            onErr: listener.onError
        });
        l.ensureConnection();
    };

    public startSetListener(path: Array<string>, listener, dataSource) {
        var l = new ListenerShell(this, dataSource, path, "set", listener);
        l.ensureConnection();
    };

    /**
     * Called by EventFirebaseConnection when connection changes. If null we should switch to publicInfo
     * else we should prefer the firebase instance provided.
     * @public
     */
    public onEventConnectionConnect(firebase) {
        this.firebase = firebase;
        this.getTelemetry().info("Firebase changed, ensuring listener connections");
        this.ensureListenerConnections();
    };

    /**
     * Called by events manager to inform us that the active auth for this event changed
     * This can mean the user logged out, or a permit is available, etc.
     *
     * Basically we want to try and fetch the eventInfo again, since now that the activeAuth has changed
     * we might get a different result

     * Can take an optional parameter called forceReload in case the userAuth is not changing but their
     * access to the event has updated
     * @public
     */
    public onActiveAuthChanged(forceReload?: boolean) {
        const activeAuthChanged = this.updateAndDiffActiveAuthKey();
        if (activeAuthChanged || forceReload) {
            this.reloadEventInfo();
            //Todo: Understand the meaning of "current user"
        }
    };

    private updateAndDiffActiveAuthKey() {
        const oldActiveAuthKey = this.activeAuthKey;
        this.activeAuthKey = this.eventsManager.getActiveAuthKey(this.getBestActiveAuth());
        return (this.activeAuthKey !== oldActiveAuthKey);
    };

    public getBestActiveAuth() {
        return this.eventsManager.getBestActiveAuthForEventId(this.getEventId());
    };

    /**
     * Connect to the data base and load the full event - not just based on the json
     * from public info. Connect should be called only on one event. Trying to call connect on other event
     * objects will result in a call to disconnect on all others.
     */
    public connect() {
        return this.eventsManager.getEventConnection().connect(this);
    };

    /**
     * Disconnect any long running connections for this event. Fall back to using public info json if it exists
     * @returns {*}
     */
    public disconnect() {
        return this.eventsManager.getEventConnection().disconnect();
    };

    public joinWithEventSecret(eventSecret) {
        return this.eventsManager.getEventBySecret(eventSecret, null);
    };

    public joinWithEventPassword(eventPassword) {
        return this.eventsManager.getEventBySecret(eventPassword, this.getEventId());
    };

    public getEventJoinSecret() {
        const api = this.getBestServiceApi();
        return api.getEventJoinSecret(this.getEventId())
    };

    /**
     * Get and saves the event secret locally and then logs out the current user
     * This is generally triggered when a user is disabled from rsvp-ing for someone else
     * because they are loggedin. In that case they will call this method to get and save the eventSecret
     * locally and the logout. This way the next person can see the event and login
     */
    public saveEventSecretAndLogout() {
        const eventId = this.getEventId();
        return this.getEventJoinSecret()
        .then((eventSecret) => this.eventsManager.saveEventSecretForEventIdAndLogout(eventId, eventSecret))
        .then(()=> this.setCurrentPersonId(null))
        .catch(()=>{
            this.setCurrentPersonId(null)
            return this.eventsManager.logout(eventId);
        });
    };

    public requestEventSecret(guestInfo) {
        const api = this.getBestServiceApi();
        return api.requestEventJoinSecret(this.getEventId(), guestInfo);
    };

    public requestAccessEmail(guest) {
        const api = this.getBestServiceApi();
        return api.requestAccessEmail(this.getEventId(), guest);
    };

    public setCurrentPersonId(personId) {
        const promise = this.eventsManager.setCurrentPersonIdForEventId(this.getEventId(), personId);
        promise.then(() => {
            return this.ensureListenerConnections();
        });
        return promise;
    };

    public getCurrentPersonId() {
        return this.eventsManager.getCurrentPersonIdForEventId(this.getEventId());
    };

    public getEventAssetStores() {
        const api = this.getBestServiceApi();
        return api.getEventAssetStores(this.getEventId(), null)
    };

    public getQuestionBankUtils() {
        return new EventQuestions(this);
    };

    public getBestServiceApi() {
        return this.eventsManager.getBestServiceApiForEvent(this);
    };

    public getServiceApiEventUserProfiles(fieldsArray) {
        const api = this.getBestServiceApi();
        const defer = this.getPromiseLib().defer();
        return api.getEventUserProfiles(this.getEventId(), fieldsArray)
    };

}
